/**
 * 2.219.0. Two things the Documents and Blog posts blocks' layouts want of a
 * page, on every surface that draws blocks (templates/_block_scripts.html):
 *
 * - **A shelf's arrows.** A shelf (`[data-shelf]`) is one row that scrolls
 *   sideways: a Documents block's Shelf and Banner cards, a Blog posts block's
 *   Overlay shelf. Each arrow is shown only when there is somewhere for it to
 *   go, and pressing it moves the row on by most of its width. Once this has
 *   run the row's scrollbar is hidden (`.shelf[data-ready]`), since the arrows
 *   stand in for it. Without this script the arrows stay hidden and the row
 *   scrolls with its scrollbar, as any overflowing row does.
 *
 * - **A post's date in the visitor's own time zone** (Kelly: "Date (published,
 *   in visitor's local time)"). The server writes the day in UTC, with the
 *   instant in `datetime`; this rewrites the words as the visitor's calendar
 *   has that instant. Without it the UTC day stands.
 *
 * Both are found again whenever blocks are drawn: the editor draws its canvas
 * after this file runs, and again on every edit.
 */

export const SHELF = "[data-shelf]";
export const TRACK = ".shelf__track";

/** The share of the row's visible width that one press moves it by: enough
 *  to bring new things into view, leaving the last one seen at the edge. */
export const STEP = 0.8;

/** Which arrows have somewhere to go. Layout is in fractions of a pixel, so
 *  within one of either end counts as there. */
export function arrowState(
  scrollLeft: number, scrollWidth: number, clientWidth: number,
): { back: boolean; on: boolean } {
  const room = scrollWidth - clientWidth;
  return { back: scrollLeft > 1, on: scrollLeft < room - 1 };
}

/** Show each of a shelf's arrows when it has somewhere to go. */
export function updateShelf(shelf: Element): void {
  const track = shelf.querySelector<HTMLElement>(TRACK);
  if (!track) return;
  shelf.setAttribute("data-ready", "");
  const state = arrowState(track.scrollLeft, track.scrollWidth, track.clientWidth);
  const back = shelf.querySelector<HTMLElement>("[data-shelf-back]");
  const on = shelf.querySelector<HTMLElement>("[data-shelf-on]");
  if (back) back.hidden = !state.back;
  if (on) on.hidden = !state.on;
}

/** Move a shelf's row on (1) or back (-1) by most of its width. */
export function moveShelf(shelf: Element, direction: 1 | -1, smooth: boolean): void {
  const track = shelf.querySelector<HTMLElement>(TRACK);
  if (!track) return;
  track.scrollBy({
    left: direction * Math.max(1, track.clientWidth * STEP),
    behavior: smooth ? "smooth" : "auto",
  });
}

/** The day `iso` falls on in `timeZone` (the visitor's own when not given),
 *  written as the visitor's language writes a short date; or null for
 *  anything that is not an instant. */
export function localDate(iso: string, locale?: string, timeZone?: string): string | null {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleDateString(locale, {
    year: "numeric", month: "short", day: "numeric", timeZone,
  });
}

/** Rewrite each post date under `root` not rewritten already. Returns how
 *  many were. Each is marked, so drawing them again does not loop. */
export function showLocalDates(root: ParentNode, locale?: string, timeZone?: string): number {
  let shown = 0;
  root.querySelectorAll<HTMLTimeElement>("time[data-local-date]:not([data-local-shown])")
    .forEach((time) => {
      time.setAttribute("data-local-shown", "");
      const words = localDate(time.getAttribute("datetime") || "", locale, timeZone);
      if (words === null) return;
      time.textContent = words;
      shown += 1;
    });
  return shown;
}


/**
 * 2.269.0. A Masonry shop's next page, as the shopper scrolls (Kelly: "As
 * the user scrolls down, new rows of images continuously load"). The pager
 * under its cards (`[data-store-more]` holding the next page's address) is
 * watched; when it comes near, that page is fetched and its cards added to
 * these, and the pager takes the page after, until there is none. Without
 * this the pager's links page as before. Returns how many cards it added.
 */
export async function loadMore(pager: HTMLElement,
                               fetchPage: (url: string) => Promise<string> = defaultFetch): Promise<number> {
  const next = pager.dataset.storeMore;
  const cards = pager.previousElementSibling;
  if (!next || !cards || !cards.matches("[data-store-cards]")) return 0;
  const lists = Array.from(document.querySelectorAll("[data-store-cards='masonry']"));
  const at = lists.indexOf(cards);
  pager.setAttribute("data-store-more-loading", "");
  try {
    const page = new DOMParser().parseFromString(await fetchPage(next), "text/html");
    const theirs = page.querySelectorAll("[data-store-cards='masonry']")[at];
    if (!theirs) return 0;
    const added = Array.from(theirs.children);
    for (const card of added) cards.appendChild(document.importNode(card, true));
    const after = theirs.nextElementSibling as HTMLElement | null;
    const further = after?.dataset?.storeMore;
    const where = after?.querySelector(".store-pages-bar__where")?.textContent;
    const mine = pager.querySelector(".store-pages-bar__where");
    if (where && mine) mine.textContent = where;
    if (further) pager.dataset.storeMore = further;
    else delete pager.dataset.storeMore;
    return added.length;
  } catch {
    return 0;
  } finally {
    pager.removeAttribute("data-store-more-loading");
  }
}

async function defaultFetch(url: string): Promise<string> {
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) throw new Error(String(response.status));
  return response.text();
}

function watchMore(): void {
  if (typeof IntersectionObserver === "undefined") return;
  const watcher = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const pager = entry.target as HTMLElement;
      if (!entry.isIntersecting || pager.hasAttribute("data-store-more-loading")) continue;
      void loadMore(pager).then(() => {
        if (!pager.dataset.storeMore) watcher.unobserve(pager);
      });
    }
  }, { rootMargin: "600px 0px" });
  document.querySelectorAll<HTMLElement>("[data-store-more]").forEach((pager) => {
    pager.classList.add("store-pages-bar--auto");
    watcher.observe(pager);
  });
}

function begin(): void {
  const sized = new WeakSet<Element>();
  const resized = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const shelf = entry.target.closest(SHELF);
      if (shelf) updateShelf(shelf);
    }
  });
  const scan = (): void => {
    document.querySelectorAll(SHELF).forEach((shelf) => {
      updateShelf(shelf);
      const track = shelf.querySelector(TRACK);
      if (track && !sized.has(track)) {
        sized.add(track);
        resized.observe(track);
      }
    });
    showLocalDates(document);
  };

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest("[data-shelf-back], [data-shelf-on]");
    const shelf = button?.closest(SHELF);
    if (!button || !shelf) return;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    moveShelf(shelf, button.hasAttribute("data-shelf-back") ? -1 : 1, !still);
  });

  // A row's own scroll does not bubble; it is heard on its way down.
  document.addEventListener("scroll", (event) => {
    const target = event.target;
    if (target instanceof Element && target.matches(TRACK)) {
      const shelf = target.closest(SHELF);
      if (shelf) updateShelf(shelf);
    }
  }, { capture: true, passive: true });

  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      scan();
    });
  }).observe(document.body, { childList: true, subtree: true });

  scan();
  watchMore();
}

// Not under the DOM tests, which import this before they have a document.
if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", begin);
  } else {
    begin();
  }
}
